import React, { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane, FaCheckCircle, FaClock, FaHeadset, FaShieldAlt, FaRocket, FaCalendarAlt, FaLinkedin, FaWhatsapp } from "react-icons/fa";
import API from "../../api";

const contactInfo = [
  { 
    icon: <FaEnvelope />, 
    label: "Email Us", 
    value: "onexvorabusinesssolutions@gmail.com", 
    subValue: "Business Inquiries & Support",
    color: "#0ea5e9", 
    href: "mailto:onexvorabusinesssolutions@gmail.com" 
  },
  { 
    icon: <FaPhone />, 
    label: "Call Us", 
    value: "+91 97180 94931 / +91 99909 96731", 
    subValue: "Mon - Sat, 10:00 AM - 7:00 PM",
    color: "#8b5cf6", 
    href: "tel:+919718094931" 
  },
  { 
    icon: <FaWhatsapp />, 
    label: "WhatsApp", 
    value: "+91 97180 94931 / +91 99909 96731", 
    subValue: "Quick Response & Support",
    color: "#25D366", 
    href: "https://wa.me/919718094931" 
  },
  { 
    icon: <FaMapMarkerAlt />, 
    label: "Visit Us", 
    value: "D-184, Sector-63, Noida, UP - 201301", 
    subValue: "Uttar Pradesh, India",
    color: "#10b981", 
    href: "https://maps.google.com/?q=D-184,+Sector-63,+Noida,+UP+-+201301,+India" 
  },
];

const budgetOptions = [
  "Under ₹10,000", "₹10,000 - ₹30,000", "₹30,000 - ₹1,00,000", 
  "₹1,00,000 - ₹3,00,000", "₹3,00,000+", "Let's Discuss",
];

const timelineOptions = [
  "ASAP (Rush Job)", "Within 1 Month", "1-3 Months", "3-6 Months", "6+ Months", "Flexible",
];

export default function Contact() {
  const ref = useRef(null);
  const formRef = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [serviceOptions, setServiceOptions] = useState([]);
  const [form, setForm] = useState({ 
    name: "", email: "", phone: "", company: "", service: "", 
    budget: "", timeline: "", message: "", hearAbout: "" 
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL
      ? `${process.env.REACT_APP_API_URL}/api`
      : "http://localhost:5000/api";
    fetch(`${apiUrl}/services`)
      .then((r) => r.json())
      .then((data) => setServiceOptions(data.map((s) => s.title)))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await API.post("/contacts", form);
      setSent(true);
      setForm({ 
        name: "", email: "", phone: "", company: "", service: "", 
        budget: "", timeline: "", message: "", hearAbout: "" 
      });
      setTimeout(() => setSent(false), 8000);
    } catch {
      setError("Something went wrong. Please try again or contact us directly via email/phone.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 sm:py-28 relative overflow-hidden w-full">
      {/* Enhanced Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/5 via-purple-900/5 to-transparent" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
        {/* Enhanced Header */}
        <motion.div 
          ref={ref} 
          initial={{ opacity: 0, y: 30 }} 
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }} 
          className="text-center mb-16"
        >
          <motion.span 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2 }}
            className="inline-block text-blue-400 font-semibold text-sm uppercase tracking-widest mb-4 px-4 py-2 glass rounded-full"
          >
            Get In Touch
          </motion.span>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black mt-4 mb-6">
            Let's Build Something <span className="gradient-text">Amazing</span>
          </h2>
          <p className="text-gray-300 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
            Ready to transform your business with cutting-edge technology? We're here to help you every step of the way. 
            Get a free consultation and project quote within 24 hours.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Left Side - Contact Info */}
          <motion.div 
            initial={{ opacity: 0, x: -40 }} 
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }} 
            className="lg:col-span-2 space-y-4 sm:space-y-6 order-2 lg:order-1 min-w-0"
          >
            <div className="glass rounded-2xl sm:rounded-3xl p-4 sm:p-6">
              <h3 className="text-white font-bold text-lg sm:text-xl mb-4 sm:mb-6">Contact Information</h3>
              <div className="space-y-3 sm:space-y-4">
                {contactInfo.map((info, i) => (
                  <motion.a 
                    key={info.label} 
                    href={info.href} 
                    target={info.label === "Visit Us" || info.label === "WhatsApp" ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, x: -20 }} 
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-all duration-300 group w-full"
                  >
                    <div 
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center text-base sm:text-lg flex-shrink-0 group-hover:scale-110 transition-transform"
                      style={{ background: `${info.color}20`, color: info.color }}
                    >
                      {info.icon}
                    </div>
                    <div className="flex-1">
                      <div className="text-gray-400 text-xs mb-1">{info.label}</div>
                      <div className="text-white font-semibold text-sm mb-1 break-all">{info.value}</div>
                      <div className="text-gray-500 text-xs">{info.subValue}</div>
                    </div>
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Enhanced Map */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="glass rounded-2xl sm:rounded-3xl overflow-hidden"
            >
              <div className="p-3 sm:p-4 border-b border-white/10">
                <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                  <FaMapMarkerAlt className="text-emerald-400" />
                  Our Office Location
                </h4>
              </div>
              <iframe
                title="ONexvora Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.0!2d77.3710!3d28.6270!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce5a218c8e0b9%3A0x6b0a7b5e5e5e5e5e!2sSector%2063%2C%20Noida%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                width="100%"
                height="180"
                style={{ border: 0, filter: "invert(90%) hue-rotate(180deg)" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="sm:h-[200px]"
              />
            </motion.div>
          </motion.div>

          {/* Right Side - Enhanced Form */}
          <motion.div 
            initial={{ opacity: 0, x: 40 }} 
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-3 order-1 lg:order-2 min-w-0"
          >
            <div className="glass rounded-2xl p-4 sm:p-8 w-full">
              {sent ? (
                <motion.div 
                  initial={{ scale: 0.8, opacity: 0 }} 
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center py-16"
                >
                  <motion.div 
                    animate={{ scale: [1, 1.1, 1] }} 
                    transition={{ duration: 0.6, repeat: 1 }}
                    className="w-24 h-24 gradient-bg rounded-full flex items-center justify-center mb-6 mx-auto"
                  >
                    <FaCheckCircle className="text-white text-4xl" />
                  </motion.div>
                  <h3 className="text-3xl font-bold text-white mb-4">Thank You!</h3>
                  <p className="text-gray-300 text-lg mb-6 max-w-md mx-auto">
                    Your inquiry has been submitted successfully. We'll get back to you within 24 hours.
                  </p>
                  <div className="flex items-center justify-center gap-4 text-sm text-gray-400">
                    <div className="flex items-center gap-2">
                      <FaCalendarAlt className="text-blue-400" />
                      <span>Response within 24 hours</span>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                  <div className="text-center mb-6 sm:mb-8">
                    <h3 className="text-white font-bold text-xl sm:text-2xl mb-2">Start Your Project</h3>
                    <p className="text-gray-400 text-sm">Fill out the form below and we'll get back to you with a detailed proposal</p>
                  </div>

                  {/* Personal Information */}
                  <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="text-gray-300 text-sm font-medium mb-2 block">Full Name *</label>
                      <input 
                        type="text" 
                        required 
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="John Doe"
                        className="w-full bg-white/5 border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-gray-300 text-sm font-medium mb-2 block">Email Address *</label>
                      <input 
                        type="email" 
                        required 
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="john@company.com"
                        className="w-full bg-white/5 border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-all text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="text-gray-300 text-sm font-medium mb-2 block">Phone Number</label>
                      <input 
                        type="tel" 
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+91 98765 43210"
                        className="w-full bg-white/5 border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-gray-300 text-sm font-medium mb-2 block">Company Name</label>
                      <input 
                        type="text" 
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                        placeholder="Your Company Ltd."
                        className="w-full bg-white/5 border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-all text-sm"
                      />
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="sm:col-span-1">
                      <label className="text-gray-300 text-sm font-medium mb-2 block">Service Required *</label>
                      <select 
                        required 
                        value={form.service}
                        onChange={(e) => setForm({ ...form, service: e.target.value })}
                        className="w-full bg-[#0f172a] border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white focus:outline-none focus:border-blue-500 transition-all text-sm appearance-none cursor-pointer"
                      >
                        <option value="" disabled>Select a service</option>
                        {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-1">
                      <label className="text-gray-300 text-sm font-medium mb-2 block">Budget Range</label>
                      <select 
                        value={form.budget}
                        onChange={(e) => setForm({ ...form, budget: e.target.value })}
                        className="w-full bg-[#0f172a] border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white focus:outline-none focus:border-blue-500 transition-all text-sm appearance-none cursor-pointer"
                      >
                        <option value="">Select budget</option>
                        {budgetOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-1">
                      <label className="text-gray-300 text-sm font-medium mb-2 block">Timeline</label>
                      <select 
                        value={form.timeline}
                        onChange={(e) => setForm({ ...form, timeline: e.target.value })}
                        className="w-full bg-[#0f172a] border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white focus:outline-none focus:border-blue-500 transition-all text-sm appearance-none cursor-pointer"
                      >
                        <option value="">Select timeline</option>
                        {timelineOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-gray-300 text-sm font-medium mb-2 block">Project Details *</label>
                    <textarea 
                      required 
                      rows={4} 
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Please describe your project requirements, goals, target audience, and any specific features you need. The more details you provide, the better we can help you."
                      className="w-full bg-white/5 border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-all text-sm resize-none"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 text-sm font-medium mb-2 block">How did you hear about us?</label>
                    <select 
                      value={form.hearAbout}
                      onChange={(e) => setForm({ ...form, hearAbout: e.target.value })}
                      className="w-full bg-[#0f172a] border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white focus:outline-none focus:border-blue-500 transition-all text-sm appearance-none cursor-pointer"
                    >
                      <option value="">Select an option</option>
                      <option value="Google Search">Google Search</option>
                      <option value="Social Media">Social Media</option>
                      <option value="Referral">Referral from Friend/Colleague</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  <motion.button 
                    type="submit" 
                    disabled={loading}
                    whileHover={{ scale: loading ? 1 : 1.02 }} 
                    whileTap={{ scale: loading ? 1 : 0.98 }}
                    className="btn-primary w-full py-3 sm:py-4 rounded-lg sm:rounded-xl text-white font-semibold flex items-center justify-center gap-3 text-base disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span>{loading ? "Submitting..." : "Submit Project Inquiry"}</span>
                    {!loading && <FaPaperPlane />}
                  </motion.button>

                  <p className="text-center text-gray-500 text-xs">
                    By submitting this form, you agree to our privacy policy. We'll never share your information with third parties.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}